import { Avatar, Badge, Button, Divider, Group, Modal, Paper, Stack, Text, Textarea, TextInput, Tooltip } from "@mantine/core";
import { toast } from "sonner";
import { useIntegrationApp } from "@membranehq/react";
import { IconCornerUpLeft } from "@tabler/icons-react";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import { useMemo, useState } from "react";

import { sendEmail } from "../../services/emailEndpoint";
import useWorkspaceStore from "../../store/workspace/workspaceStore";
import { getEmailViewModel, humanizeEmailTag, type EmailRecordLike } from "./emailUtils";
import type { AssociatedObject } from "./types";

function ensureBaseTargetBlank(html: string): string {
  if (/<base\b/i.test(html)) return html;

  if (/<head\b[^>]*>/i.test(html)) {
    return html.replace(/<head\b[^>]*>/i, (match) => `${match}<base target="_blank" />`);
  }

  if (/<html\b[^>]*>/i.test(html)) {
    return html.replace(/<html\b[^>]*>/i, (match) => `${match}<head><base target="_blank" /></head>`);
  }

  return `<!doctype html><html><head><base target="_blank" /></head><body>${html}</body></html>`;
}

type EmailRecord = Pick<AssociatedObject, "meta_data" | "created_at" | "key_fields_text" | "thread_id"> | EmailRecordLike;

const EmailDetailsPanel = ({ email }: { email: EmailRecord | null }) => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const integrationApp = useIntegrationApp();
  const view = useMemo(() => (email ? getEmailViewModel(email) : null), [email]);

  // Reply functionality state
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Initialize reply subject when email changes
  useMemo(() => {
    if (view?.subject) {
      const subject = view.subject.startsWith('Re: ') ? view.subject : `Re: ${view.subject}`;
      setReplySubject(subject);
    }
  }, [view?.subject]);

  const handleReplyClick = () => {
    setIsReplyOpen(true);
  };

  const handleSendReply = async () => {
    if (!workspace?.id || !email || !view || !replyBody.trim() || !replySubject.trim()) return;

    // Debug logging
    const metadata = email.meta_data as Record<string, unknown>;
    console.log('Reply attempt:', {
      senderEmail: view.sender.email,
      senderDisplay: view.sender.display,
      sender: view.sender,
      metadata: email.meta_data,
      metadataSender: metadata?.sender,
      metadataNativeSenderEmail: metadata?.native_sender_email,
      metadataFrom: metadata?.from,
      emailKeyFields: email.key_fields_text
    });

    // Validate that sender email exists
    if (!view.sender.email) {
      toast.error("Cannot Reply", { description: "Unable to reply to this email because the sender's email address is not available." });
      return;
    }

    setIsSending(true);
    try {
      // Get Gmail connection ID (assuming it's available in the integration app context)
      const gmailConnection = await integrationApp.connection('gmail').get();
      const connectionId = gmailConnection?.id;

      if (!connectionId) {
        toast.error("Connection Error", { description: "No Gmail connection found. Please connect your Gmail account first." });
        return;
      }

      // Debug logging for thread handling
      console.log('Sending reply with thread info:', {
        originalThreadId: email.thread_id,
        subject: replySubject,
        to: view.sender.email
      });

      await sendEmail({
        workspace_id: workspace.id,
        connectionId,
        to: [view.sender.email], // Reply to the sender
        subject: replySubject,
        html_body: replyBody.replace(/\n/g, '<br>'), // Convert line breaks to HTML
        thread_id: email.thread_id || undefined, // Use the original thread ID for reply
      });

      toast.success("Email Sent", { description: "Your reply has been sent successfully." });

      // Reset form
      setReplyBody("");
      setIsReplyOpen(false);
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error("Send Failed", { description: "Failed to send email. Please try again." });
    } finally {
      setIsSending(false);
    }
  };

  const handleSendReplyClick = () => {
    handleSendReply().catch((error) => {
      console.error('Failed to send reply:', error);
    });
  };

  const formattedDate = useMemo(() => {
    if (!view?.timestamp) return "";
    const date = new Date(view.timestamp);
    if (Number.isNaN(date.getTime())) return "";
    return format(date, "MMM d, yyyy 'at' h:mm a");
  }, [view?.timestamp]);

  const iframeSrcDoc = useMemo(() => {
    if (!view?.htmlBody) return "";
    const sanitized = DOMPurify.sanitize(view.htmlBody, { USE_PROFILES: { html: true } });
    return ensureBaseTargetBlank(sanitized);
  }, [view?.htmlBody]);

  const avatarLabel = useMemo(() => {
    if (!view) return "?";
    return (view.sender.name || view.sender.email || view.sender.display || "?").slice(0, 1).toUpperCase();
  }, [view]);

  if (!view) {
    return (
      <Text size="sm" c="text.5">
        Select an email to view details.
      </Text>
    );
  }

  return (
    <Stack gap="sm" h="100%">
      {/* Header Section */}
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text size="lg" fw={700} c="text.9" lh={1.3} style={{ flex: 1 }}>
            {view.subject}
          </Text>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconCornerUpLeft size={14} />}
            onClick={handleReplyClick}
            disabled={!view?.sender.email}
          >
            Reply
          </Button>
        </Group>

        <Group justify="space-between" align="center" wrap="wrap">
          <Group gap="sm">
            <Avatar radius="xl" size={32} color="blue">
              {avatarLabel}
            </Avatar>
            <Stack gap={0}>
              <Text size="sm" fw={600} c="text.9">
                {view.sender.display}
              </Text>
              <Text size="xs" c="text.5">
                {view.sender.email}
              </Text>
            </Stack>
          </Group>
          {formattedDate && (
            <Text size="xs" c="text.5">
              {formattedDate}
            </Text>
          )}
        </Group>

        {view.recipients.length > 0 && (
          <Group gap={6}>
            <Text size="xs" c="text.5" fw={500}>To:</Text>
            {view.recipients.map((recipient) => (
              <Badge key={recipient} variant="default" size="sm" radius="sm" fw={500} tt="none" c="text.7" bg="gray.1">
                {recipient}
              </Badge>
            ))}
          </Group>
        )}

        {(view.direction || view.labels.length > 0) && (
          <Group gap={6} wrap="wrap">
            {view.direction && (
              <Badge variant="outline" size="sm" radius="md" color="gray">
                {humanizeEmailTag(view.direction)}
              </Badge>
            )}
            {view.labels.slice(0, 8).map((label) => (
              <Badge key={label} variant="outline" size="sm" radius="md" color="gray" title={label}>
                {humanizeEmailTag(label)}
              </Badge>
            ))}
            {view.labels.length > 8 && (
              <Tooltip label={view.labels.slice(8).join(', ')}>
                <Badge variant="outline" size="sm" radius="md" color="gray">
                  +{view.labels.length - 8} more
                </Badge>
              </Tooltip>
            )}
          </Group>
        )}
      </Stack>

      <Divider />

        {view.htmlBody ? (
          <Paper withBorder className="h-[calc(100vh-300px)]" radius="md" p={0}>
            <iframe
            className="w-full h-full"
              title="Email HTML"
              style={{ border: 'none', backgroundColor: 'white' }}
              sandbox="allow-popups"
              referrerPolicy="no-referrer"
              srcDoc={iframeSrcDoc}
            />
          </Paper>
        ) : (
          <Paper withBorder radius="md" p="md">
            <Text size="sm" c="text.6">
              No HTML content is available for this email.
            </Text>
          </Paper>
        )}

      {/* Reply Modal */}
      <Modal
        opened={isReplyOpen}
        onClose={() => setIsReplyOpen(false)}
        title={<Text fw={600} size="lg">Reply to {view.sender.display}</Text>}
        size="lg"
        padding="md"
      >
        <Stack gap="md">
          <TextInput
            label="Subject"
            value={replySubject}
            onChange={(e) => setReplySubject(e.currentTarget.value)}
            size="sm"
            data-autofocus
          />
          <Textarea
            label="Message"
            placeholder="Type your reply here..."
            value={replyBody}
            onChange={(e) => setReplyBody(e.currentTarget.value)}
            minRows={8}
            maxRows={12}
            autosize
            size="sm"
            required
          />
          <Group justify="flex-end" gap="xs" mt="sm">
            <Button
              variant="default"
              onClick={() => setIsReplyOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendReplyClick}
              loading={isSending}
              disabled={!replyBody.trim()}
            >
              Send Reply
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default EmailDetailsPanel;
